import React, { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import "./About.css";
import KrisKatrien from "../../assets/img/KrisKatrien.jpg";

const LUXURY = [0.16, 1, 0.3, 1];

const milestones = [
  {
    year: "2018",
    title: "Oprichting",
    description: "Kris Schoenmakers richtte Carrosserie Kris op",
  },
  {
    year: "2020",
    title: "Uitbreiding team",
    description: "Katrien sluit zich aan bij het bedrijf",
  },
  {
    year: "2023",
    title: "Nieuwe locatie",
    description: "Verhuizing naar modern bedrijvencomplex",
  },
  {
    year: "2023",
    title: "Eurogarant label",
    description: "Behaalde het Eurogarant kwaliteitslabel",
  },
  {
    year: "2024",
    title: "Verzekeringen en leasing",
    description: "Gestart met werken voor verzekeringen en leasing"
  },
  {
    year: "September 2024",
    title: "Uitbreiding team",
    description: "Zoon Kobe Schoenmakers gestart met duaal leren in onze carrosserie."
  },
  {
    year: "April 2026",
    title: "Arval Award Bodyshop 2025",
    description: "Carrosserie Kris behaalt zilver",
  },
  {
    year: "2026",
    title: "ABS partner",
    description: "Juni 2026 aangesloten bij het netwerk ABS",
  }

];

export default function About() {
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const imageRef = useRef(null);

  const isInView = useInView(sectionRef, { once: true, margin: "-10%" });
  const timelineInView = useInView(timelineRef, { once: true, margin: "-15%" });

  /* Subtle image parallax */
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="about" className="about-section" ref={sectionRef}>
      <div className="about-container">
        {/* ── Header ── */}
        <div className="about-header">
          <motion.span
            className="section-badge"
            initial={{ opacity: 0, y: 12 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: LUXURY }}
          >
            Over ons
          </motion.span>
          <motion.h2
            className="about-title"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.12, ease: LUXURY }}
          >
            Ons verhaal
          </motion.h2>
        </div>

        {/* ── Main grid: image left / text right ── */}
        <div className="about-main">
          {/* Image with offset industrial frame */}
          <motion.div
            className="about-image-section"
            ref={imageRef}
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: LUXURY }}
          >
            <div className="image-frame-wrapper">
              <motion.img
                src={KrisKatrien}
                alt="Kris en Katrien – Carrosserie Kris"
                className="about-image"
                style={{ y: imageY }}
              />
            </div>
          </motion.div>

          {/* Text content */}
          <motion.div
            className="about-content-section"
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.35, ease: LUXURY }}
          >
            <motion.p
              className="story-paragraph"
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.5, ease: LUXURY }}
            >
              Carrosserie Kris werd in 2018 opgericht door Kris Schoenmakers.
              Een ondernemingsavontuur dat uit het niets werd opgebouwd met
              passie en toewijding. Sinds 2020 vormt Katrien een essentieel
              onderdeel van het team.
            </motion.p>

            <motion.p
              className="story-paragraph"
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.62, ease: LUXURY }}
            >
              In 2023 verhuisden we naar een moderne locatie in een
              bedrijvencomplex, strategisch gelegen nabij afrit 20 van de E313.
              Met ruime parking, laadpalen en het Eurogarant kwaliteitslabel
              zijn we klaar voor de toekomst.
            </motion.p>

            {/* Technical spec box */}
            <motion.div
              className="highlight-box"
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.76, ease: LUXURY }}
            >
              <span className="spec-tag">Elektrisch &amp; hybride</span>
              <p className="spec-body">
                Continue bijscholing zodat wij elk modern voertuig correct en
                veilig kunnen herstellen.
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* ── Timeline ── */}
        <div className="timeline-section" ref={timelineRef}>
          <motion.h3
            className="timeline-title"
            initial={{ opacity: 0, y: 20 }}
            animate={timelineInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: LUXURY }}
          >
            Onze mijlpalen
          </motion.h3>

          <div className="timeline">
            {/* Animated draw line */}
            <motion.div
              className="timeline-line"
              initial={{ scaleY: 0 }}
              animate={timelineInView ? { scaleY: 1 } : {}}
              transition={{ duration: 1.2, delay: 0.3, ease: LUXURY }}
            />

            {milestones.map((m, i) => (
              <motion.div
                key={i}
                className={`timeline-item${i % 2 === 0 ? " left" : " right"}`}
                initial={{ opacity: 0, x: i % 2 === 0 ? -32 : 32 }}
                animate={timelineInView ? { opacity: 1, x: 0 } : {}}
                transition={{
                  duration: 0.65,
                  delay: 0.5 + i * 0.12,
                  ease: LUXURY,
                }}
              >
                <div className="timeline-dot" />
                <div className="timeline-year">{m.year}</div>
                <div className="timeline-content">
                  <h4>{m.title}</h4>
                  <p>{m.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
